import shuffle from "lodash/shuffle";

import { MessageFilter } from "../MessageFilter";

import type { ColumnData, ReaderLike, Value } from "./ReaderLike";

export interface ZipperOpts {
  filter: {
    rowIndexFilter: MessageFilter<number>;
    startReadPosition: number;
  };
  signal: AbortSignal;
}

/**
 * The Zipper combines data from multiple ReaderLike instances into row objects.
 *
 * For example, if reading columns "timestamp" and "metadata_map" where metadata_map is
 * a struct with fields {device, location}, the zipper produces:
 * { timestamp: 1234567890, metadata_map: { device: "foo", location: "bar" } }
 */
class Zipper {
  private columnToData: Record<string, ColumnData[]>; // columnName -> chunks of columnValues
  private done = false;
  private rowIndexFilter: MessageFilter<number> | undefined = undefined;
  private readPosition: number; // current row index
  private _rowCount: bigint | undefined = undefined;
  private readers: ReaderLike[];
  private readerToDataGenerator: Map<
    ReaderLike,
    ReturnType<ReaderLike["iterColumnChunks"]>
  >;

  constructor(readers: ReaderLike[], options?: Partial<ZipperOpts>) {
    this.columnToData = {};

    this.readPosition = options?.filter?.startReadPosition ?? 0;
    this.rowIndexFilter = options?.filter?.rowIndexFilter;
    this.readers = readers;
    this.assertReadersCanBeZippedTogether();

    this.readerToDataGenerator = new Map();

    for (const reader of readers) {
      this.readerToDataGenerator.set(
        reader,
        reader.iterColumnChunks({
          rowIndexFilter: this.rowIndexFilter,
          signal: options?.signal,
        }),
      );
      for (const column of reader.columns) {
        this.columnToData[column] = [];
      }
    }
  }

  private get rowCount() {
    if (this._rowCount === undefined) {
      this._rowCount = this.readers[0]?.rowCount ?? 0n;
    }
    return this._rowCount;
  }

  public getNext(): [Record<string, Value>, number] {
    const combined: Record<string, Value> = {};
    const rowIndex = this.readPosition;
    for (const reader of this.readers) {
      for (const column of reader.columns) {
        const value = this.columnValueAtCurrentRowIndex(column);
        if (value !== undefined) {
          combined[column] = value;
        }
      }
    }

    this.setReadPosition();
    return [combined, rowIndex];
  }

  public async hasMore(): Promise<boolean> {
    await this.loadMoreIfNeeded();
    return this.done === false;
  }

  public async initialize() {
    await this.loadMoreIfNeeded();
  }

  private assertReadersCanBeZippedTogether() {
    const expected = this.rowCount;
    const ok = this.readers.every((r) => r.rowCount === expected);
    if (!ok) {
      throw new Error(
        "Cannot zip Parquet readers: differing row counts detected",
      );
    }
  }

  private columnValueAtCurrentRowIndex(column: string): Value | undefined {
    // find chunk for this column that contains the row value for the current position
    const index = this.columnToData[column].findIndex(
      (chunk) =>
        this.readPosition >= chunk.rowStart && this.readPosition < chunk.rowEnd,
    );
    if (index === -1) {
      // Should be an impossible code path.
      // Hitting this means we're attempting to read beyond the rowCount of the Parquet file.
      return undefined;
    }
    const chunk = this.columnToData[column][index];

    // Remove any chunks that are no longer needed
    this.columnToData[column] = this.columnToData[column].slice(index);

    return chunk.columnData[this.readPosition - chunk.rowStart];
  }

  private async loadMoreIfNeeded() {
    loading: for (const [
      reader,
      generator,
    ] of this.readerToDataGenerator.entries()) {
      for (const column of reader.columns) {
        const lastIndexOfDataForThisColumn =
          this.columnToData[column].at(-1)?.rowEnd ?? 0;
        if (lastIndexOfDataForThisColumn > this.readPosition) {
          // we have sufficient data for this column to keep yielding messages for now
          continue;
        }

        while (true) {
          const result = await generator.next();
          if (result.done) {
            // reader is exhausted
            for (const column of reader.columns) {
              this.columnToData[column].length = 0;
            }
            continue loading;
          }

          const page = result.value;
          this.columnToData[page.columnName].push(page);

          // Any given column chunk may or may not contain column values for `column`
          // If it does, short-circuit.
          // If not, continue reading until column data for `column` loads.
          if (page.columnName === column) {
            break;
          }
        }
      }
    }

    // if there is no data left for any column after loading more data, we're done
    const hasMore = Object.values(this.columnToData).some(
      (chunks) => chunks.length > 0,
    );
    if (!hasMore) {
      this.done = true;
    }
  }

  private setReadPosition() {
    if (this.done) {
      return;
    }

    const currentRowStarts = Math.min(
      ...Object.values(this.columnToData)
        .map((chunk) => chunk[0]?.rowStart)
        .filter((start) => start !== undefined),
    );
    if (this.readPosition < currentRowStarts) {
      this.readPosition = currentRowStarts;
    } else if (!this.rowIndexFilter) {
      this.readPosition += 1;
    } else {
      while (this.readPosition < this.rowCount) {
        this.readPosition += 1;
        if (this.rowIndexFilter.matches(this.readPosition)) {
          break;
        }
        if (this.rowIndexFilter.exceedsFilter(this.readPosition)) {
          this.done = true;
          break;
        }
      }
    }
  }
}

export async function* zipColumnData(
  readers: ReaderLike[],
  options?: Partial<ZipperOpts>,
) {
  // GM (2025-06)
  // By shuffling the readers, multiple panels that visualize the same signal will more cooperatively multitask
  // the work of downloading the bytes for that signal, if not already downloaded.
  const shuffled = shuffle(readers);
  const zipper = new Zipper(shuffled, options);
  await zipper.initialize();

  while (true) {
    options?.signal?.throwIfAborted();

    const hasMore = await zipper.hasMore();
    if (!hasMore) {
      return;
    }

    yield zipper.getNext();
  }
}
